import type { QueryClient } from '@tanstack/react-query';
import { conversationQueryOptions } from './conversation-query.util';
import type { CanonicalConversationEnrichmentRecovery } from '../../types/canonical-conversation-recovery.types';

/**
 * Carries a pending related-data reason across a replacement canonical read.
 * @param queryClient - Account-owned cache containing the original request.
 * @param conversationId - Conversation being recovered.
 * @param tenantId - Tenant whose exact recovery key may contribute work.
 * @returns Unexpired reason with still-owned instances, or no active reason.
 */
export const getConversationRecoveryEnrichment = (
  queryClient: QueryClient,
  conversationId: number,
  tenantId: string
): CanonicalConversationEnrichmentRecovery | undefined => {
  const recovery = queryClient.getQueryCache().find({
    queryKey: conversationRecoveryQueryKey(conversationId, tenantId),
    exact: true,
  });
  const enrichment: unknown = recovery?.meta?.enrichment;
  if (
    !recovery ||
    recovery.state.fetchStatus === 'idle' ||
    typeof enrichment !== 'object' ||
    enrichment === null ||
    !('deadline' in enrichment) ||
    typeof enrichment.deadline !== 'number' ||
    !Number.isFinite(enrichment.deadline) ||
    Date.now() >= enrichment.deadline ||
    !('scope' in enrichment) ||
    !(enrichment.scope instanceof Set)
  ) {
    return;
  }
  const scope = enrichment.scope;
  return {
    deadline: enrichment.deadline,
    scope: new Set(
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['crm/documents/funnel-conversations'] })
        .filter((query) => scope.has(query))
    ),
  };
};

/**
 * Identifies the temporary recovery owned by one account's canonical detail query.
 * @param conversationId - Authorized CRM conversation identity.
 * @param tenantId - Tenant whose response can satisfy this recovery.
 * @returns Child key cancelled alongside ordinary conversation reads.
 */
export const conversationRecoveryQueryKey = (
  conversationId: number,
  tenantId: string
) => [
  ...conversationQueryOptions(conversationId).queryKey,
  'recovery',
  tenantId,
];

/**
 * Retains active recovery authority independently of bounded socket-history eviction.
 * Settled, cancelled, removed and expired queries no longer contribute authority.
 * @param queryClient - Account-owned cache containing temporary recovery work.
 * @param conversationId - Authorized CRM conversation identity.
 * @param tenantId - Tenant whose active recovery must be protected.
 * @returns Minimum revision timestamp, or zero when no bounded recovery is active.
 */
export const getConversationRecoveryMinimum = (
  queryClient: QueryClient,
  conversationId: number,
  tenantId: string
): number => {
  const recovery = queryClient.getQueryCache().find({
    queryKey: conversationRecoveryQueryKey(conversationId, tenantId),
    exact: true,
  });
  const minimum = recovery?.meta?.minimumRevision;
  const deadline = recovery?.meta?.deadline;
  return recovery?.state.fetchStatus !== 'idle' &&
    typeof minimum === 'number' &&
    Number.isFinite(minimum) &&
    typeof deadline === 'number' &&
    Date.now() < deadline
    ? minimum
    : 0;
};
